import { useGetList, useGetOne, type Identifier } from "ra-core";
import { ArrowRight, CalendarDays, MessageSquare, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { saleName, useActiveSales } from "../../companies/NextStepDialog";
import { Kbd } from "../../layout/Kbd";
import { formatDateShort, osloDateKey } from "../../misc/osloDate";
import { daysBetween } from "../../misc/workdays";
import { useConfigurationContext } from "../../root/ConfigurationContext";
import type {
  Company,
  CompanyNextStep,
  ContactType,
  CustomerTimelineEvent,
  Deal,
  Quote,
  Sale,
  WorkItem,
} from "../../types";
import { pickNextStep } from "../../deals/nextStep";
import type { DriftScan } from "./buildRows";
import { formatValue } from "./ranking";

// Kundepanelet (M2): åpnes i høyre kolonne når en rad velges med klikk eller
// Enter. Viser ansvarlig, Sist kontakt, Neste steg, Mulighet, Tilbud, Drift,
// de fire siste hendelsene og åpne oppgaver. Esc eller lukkeknappen lukker,
// og fokus går tilbake til raden. Panelet låser ikke fokus (ikke modalt).

const KIND_LABEL: Record<ContactType, string> = {
  call: "Ring",
  meeting: "Møte",
  email: "E-post",
  other: "Kontakt",
};

const QUOTE_STATUS: Record<Quote["status"], string> = {
  draft: "Utkast",
  sent: "Sendt",
  accepted: "Akseptert",
  declined: "Avslått",
};

const dealValue = (deal: Deal) => {
  const lines = deal.annual_value ?? null;
  return lines ?? deal.amount ?? null;
};

export const CustomerPanel = ({
  companyId,
  openDeals,
  tasks,
  drift,
  onClose,
  onPlan,
  onContact,
}: {
  companyId: Identifier;
  openDeals: Deal[];
  tasks: WorkItem[];
  drift: DriftScan | null;
  onClose: () => void;
  onPlan: (deal: Deal | null) => void;
  onContact: () => void;
}) => {
  const ref = useRef<HTMLElement>(null);
  const { dealStages } = useConfigurationContext();
  const { data: sales = [] } = useActiveSales();
  const today = osloDateKey();
  const {
    data: company,
    isPending,
    error,
  } = useGetOne<Company>("companies", { id: companyId });
  const { data: lastContacts = [] } = useGetList<{
    id: Identifier;
    company_id: Identifier;
    last_contact_at: string | null;
    last_contact_kind?: ContactType | null;
  }>("company_last_contact", {
    filter: { company_id: companyId },
    pagination: { page: 1, perPage: 1 },
    sort: { field: "company_id", order: "ASC" },
  });
  const { data: steps = [] } = useGetList<CompanyNextStep>(
    "company_next_steps",
    {
      filter: { company_id: companyId, "completed_at@is": null },
      pagination: { page: 1, perPage: 1 },
      sort: { field: "id", order: "DESC" },
    },
  );
  const { data: events = [], isPending: eventsPending } =
    useGetList<CustomerTimelineEvent>("customer_timeline", {
      filter: { company_id: companyId },
      sort: { field: "ts", order: "DESC" },
      pagination: { page: 1, perPage: 4 },
    });
  const deals = openDeals.filter(
    (d) => String(d.company_id) === String(companyId),
  );
  const { data: quotes = [] } = useGetList<Quote>(
    "quotes",
    {
      filter: { "deal_id@in": `(${deals.map((d) => d.id).join(",")})` },
      sort: { field: "created_at", order: "DESC" },
      pagination: { page: 1, perPage: 1 },
    },
    { enabled: deals.length > 0 },
  );

  // Fokus inn ved åpning. Et bytte av kunde (J/K i lista) lar fokus stå
  // på raden.
  useEffect(() => {
    ref.current?.focus({ preventScroll: true });
  }, []);

  const titleId = `kundepanel-${companyId}`;
  const shell = (children: React.ReactNode, name?: string) => (
    <section
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-labelledby={name ? titleId : undefined}
      aria-label={name ? undefined : "Kundepanel"}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key === "Escape" && !event.defaultPrevented) {
          event.preventDefault();
          onClose();
        }
      }}
      className="flex flex-col gap-4 rounded-[10px] border bg-card p-5 text-[13px] outline-none"
    >
      {children}
    </section>
  );

  if (isPending) {
    return shell(
      <div aria-busy="true" className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
        {Array.from({ length: 5 }, (_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>,
    );
  }

  if (error || !company) {
    return shell(
      <div role="alert" className="flex flex-col gap-3">
        <p className="font-semibold">Kunne ikke hente kunden</p>
        <p className="text-muted-foreground">
          {(error as Error | null)?.message ?? "Ukjent feil"}
        </p>
        <Button variant="outline" size="sm" onClick={onClose}>
          Lukk
        </Button>
      </div>,
    );
  }

  const owner = saleName(sales as Sale[], company.sales_id);
  const lc = lastContacts.find(
    (r) => String(r.company_id) === String(companyId),
  );
  const step = steps.find(
    (s) => !s.completed_at && String(s.company_id) === String(companyId),
  );
  const deal = [...deals].sort(
    (a, b) => (dealValue(b) ?? 0) - (dealValue(a) ?? 0),
  )[0];
  const dealTasks = deal
    ? tasks.filter((t) => String(t.deal_id) === String(deal.id))
    : [];
  const dealNext = deal ? pickNextStep(dealTasks) : undefined;
  const stage = deal
    ? dealStages.find((s) => s.value === deal.stage)?.label
    : null;
  const stageDays =
    deal?.stage_changed_at != null
      ? daysBetween(osloDateKey(new Date(deal.stage_changed_at)), today)
      : null;
  const quote = quotes.find((q) =>
    deals.some((d) => String(d.id) === String(q.deal_id)),
  );
  const openTasks = tasks
    .filter(
      (t) =>
        !t.completed_at &&
        String(t.company_id) === String(companyId) &&
        t.deal_id == null,
    )
    .slice(0, 4);

  const nextStepText = dealNext
    ? `${dealNext.title}${dealNext.due_date ? `, ${formatDateShort(dealNext.due_date)}` : ""}`
    : step?.description
      ? `${step.description}${step.due_date ? `, ${formatDateShort(step.due_date)}` : ""}`
      : null;
  const missingStep = !nextStepText && !!deal;
  const initials = company.name
    .split(/\s+/)
    .filter((part) => /^[\p{L}\d]/u.test(part))
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join("");
  const value = deal ? dealValue(deal) : null;

  return shell(
    <>
      <div className="flex items-start gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-[8px] border text-xs font-medium text-muted-foreground">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <h2 id={titleId} className="truncate text-[15px] font-semibold">
            {company.name}
          </h2>
          <p className="text-xs text-muted-foreground">
            Kunde · {owner ? `ansvarlig ${owner}` : "uten ansvarlig"}
          </p>
        </div>
        <span className="hidden md:inline">
          <Kbd>Esc</Kbd>
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onClose}
          aria-label="Lukk kundepanelet"
        >
          <X className="size-4" />
        </Button>
      </div>

      <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-3 gap-y-2">
        <dt className="text-muted-foreground">Sist kontakt</dt>
        <dd>
          {lc?.last_contact_at
            ? `${lc.last_contact_kind ? `${KIND_LABEL[lc.last_contact_kind] ?? "Kontakt"}, ` : ""}${formatDateShort(lc.last_contact_at)}`
            : "Aldri"}
        </dd>
        <dt className="text-muted-foreground">Neste steg</dt>
        <dd className={missingStep ? "text-red-500" : undefined}>
          {nextStepText ?? (missingStep ? "Mangler" : "Ingen")}
        </dd>
        <dt className="text-muted-foreground">Mulighet</dt>
        <dd>
          {deal
            ? [
                stage,
                value ? formatValue(value) : null,
                stageDays != null ? `${stageDays} dager` : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : "Ingen åpen"}
          {deals.length > 1 && (
            <span className="text-muted-foreground">
              {" "}
              (+{deals.length - 1})
            </span>
          )}
        </dd>
        <dt className="text-muted-foreground">Tilbud</dt>
        <dd>
          {quote
            ? [
                quote.status === "sent" && quote.sent_at
                  ? `Sendt ${formatDateShort(quote.sent_at)}`
                  : QUOTE_STATUS[quote.status],
                quote.viewed_at
                  ? `åpnet ${formatDateShort(quote.viewed_at)}`
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")
            : "Ingen"}
        </dd>
        <dt className="text-muted-foreground">Drift</dt>
        <dd>
          {drift?.findings.length
            ? `${drift.findings.length} funn, skannet ${formatDateShort(drift.scannedAt)}`
            : "Ingen funn"}
        </dd>
      </dl>

      <div className="flex flex-col gap-2 border-t pt-4">
        <h3 className="font-semibold">Siste hendelser</h3>
        {eventsPending ? (
          <Skeleton className="h-16 w-full" />
        ) : events.length === 0 ? (
          <p className="text-muted-foreground">Ingen hendelser ennå</p>
        ) : (
          <ul className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 gap-y-1.5">
            {events.slice(0, 4).map((e) => (
              <li key={e.id} className="contents">
                <span className="text-muted-foreground tabular-nums">
                  {formatDateShort(e.ts)}
                </span>
                <span className="truncate" title={e.title}>
                  {e.title}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t pt-4">
        <h3 className="font-semibold">Åpne oppgaver</h3>
        {openTasks.length === 0 ? (
          <p className="text-muted-foreground">Ingen åpne oppgaver på kunden</p>
        ) : (
          <ul className="grid grid-cols-[84px_minmax(0,1fr)] gap-x-3 gap-y-1.5">
            {openTasks.map((t) => (
              <li key={t.id} className="contents">
                <span className="text-muted-foreground tabular-nums">
                  {t.due_date ? formatDateShort(t.due_date) : "—"}
                </span>
                <Link
                  to={`/work_items/${t.id}`}
                  className="truncate hover:underline"
                  title={t.title}
                >
                  {t.title}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2 border-t pt-4">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => onPlan(deal ?? null)}>
            <CalendarDays aria-hidden className="size-4" />
            Planlegg neste steg
          </Button>
          <Button size="sm" variant="outline" onClick={onContact}>
            <MessageSquare aria-hidden className="size-4" />
            Registrer kontakt
          </Button>
        </div>
        <Link
          to={`/companies/${company.id}/show`}
          className="flex w-fit items-center gap-1.5 px-1 py-1 text-[13px] hover:underline"
        >
          Åpne kunde
          <ArrowRight aria-hidden className="size-3.5" />
        </Link>
      </div>
    </>,
    company.name,
  );
};
